import React from 'react';

/**
 * Product marks, drawn inline rather than loaded from /public.
 *
 * An SVG referenced as `<img src="…">` renders in an isolated document with no
 * access to the page's webfonts, so a wordmark set in Nunito would silently fall
 * back to the platform sans. Inlining keeps the type correct.
 *
 * These are faithful rebuilds of the supplied marks, not the original artwork —
 * the wordmarks are set in Nunito Black rather than each brand's own face. To
 * swap in the real files, replace the body of a component with
 * `<img src="/logos/<name>.svg" alt="" width={size} height={size} />`.
 */

const SPARKLE = 'M50 0C53 26 74 47 100 50C74 53 53 74 50 100C47 74 26 53 0 50C26 47 47 26 50 0Z';

export const SEVIL_CREAM = '#fcf4e8';
export const SEVIL_BROWN = '#3d2b23';
export const GG_BLUE = '#2563eb';

/** The sevil. mark: cream ground, brown wordmark, two twinkling sparkles. */
export function SevilLogo({ size = 96, animated = true }: { size?: number; animated?: boolean }) {
  return (
    <svg
      viewBox="0 0 512 512"
      width={size}
      height={size}
      role="img"
      aria-label="sevil."
      style={{ display: 'block', borderRadius: '22%' }}
    >
      <rect width="512" height="512" rx="112" fill={SEVIL_CREAM} />

      {/*
        Placement lives on the wrapping <g> and the twinkle on the <path> inside it.
        They cannot share an element: a CSS `transform` in the keyframe replaces the
        SVG `transform` attribute outright rather than composing with it, which
        threw both sparkles back to the origin. `fill-box` then scales each one
        about its own centre instead of the viewBox's.
      */}
      <g fill={SEVIL_BROWN}>
        <g transform="translate(312 66) scale(1.01)">
          <path
            d={SPARKLE}
            style={
              animated
                ? { animation: 'duoTwinkle 2.6s ease-in-out infinite', transformBox: 'fill-box', transformOrigin: 'center' }
                : undefined
            }
          />
        </g>
        <g transform="translate(397 141) scale(0.51)">
          <path
            d={SPARKLE}
            style={
              animated
                ? { animation: 'duoTwinkle 2.6s ease-in-out 1.3s infinite', transformBox: 'fill-box', transformOrigin: 'center' }
                : undefined
            }
          />
        </g>
      </g>

      <text
        x="62"
        y="428"
        fill={SEVIL_BROWN}
        fontFamily="Nunito, ui-rounded, sans-serif"
        fontWeight="900"
        fontSize="160"
        letterSpacing="-5"
      >
        sevil.
      </text>
    </svg>
  );
}

/** The russian.gg mark: blue ground, white double-g. */
export function RussianGgLogo({ size = 96 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 512 512"
      width={size}
      height={size}
      role="img"
      aria-label="russian.gg"
      style={{ display: 'block', borderRadius: '22%' }}
    >
      <rect width="512" height="512" rx="112" fill={GG_BLUE} />
      <text
        x="256"
        y="352"
        textAnchor="middle"
        fill="#ffffff"
        fontFamily="Nunito, ui-rounded, sans-serif"
        fontWeight="900"
        fontSize="270"
        letterSpacing="-10"
      >
        gg
      </text>
    </svg>
  );
}

/** The wordmark's near-black, taken from the supplied lockup. */
export const VAQTLY_INK = '#22272e';

/**
 * The Vaqtly mark: a blue squircle carrying a two-stroke check.
 *
 * Geometry measured off the supplied artwork rather than eyeballed, because the
 * first attempt at this was a symmetric V and the mark is not symmetric — it is
 * a check. Scanning the source for horizontal runs of light pixels gives, as a
 * fraction of the tile:
 *
 *   left arm    centre 33.2% → 48.5%,  cap top at y 40%,   stroke 17%
 *   right arm   centre 68.6% → 48.5%,  cap top at y 34.5%, stroke 18.5%
 *   vertex      (48.5%, 67%)
 *
 * So the right arm starts higher, runs longer and is the thicker of the two.
 *
 * The arms are separate strokes rather than one polyline so the right one can
 * take its own white-to-pale-blue gradient — that fade is what makes the two
 * read as one ribbon folding behind itself, and a single path carries a single
 * paint. Right is drawn first, so the solid white left arm covers it at the
 * vertex, matching the original.
 *
 * Gradient ids are per-instance: the mark appears in the header and the footer,
 * and two identical `id`s in one document is invalid — the first would silently
 * win for both.
 */
export function VaqtlyMark({ size = 36 }: { size?: number }) {
  const uid = React.useId().replace(/:/g, '');
  const bg = `vq-bg-${uid}`;
  const arm = `vq-arm-${uid}`;

  return (
    <svg viewBox="0 0 512 512" width={size} height={size} aria-hidden="true" style={{ display: 'block' }}>
      <defs>
        {/*
          Radial, not linear. Sampling the four corners gives #00a7fc top-left
          against #0067–0070fc at the other three: the light is pooled in one
          corner and falls off fast, which a diagonal linear ramp renders as a
          far too bright middle.
        */}
        <radialGradient id={bg} cx="0.08" cy="0" r="1.15">
          <stop offset="0" stopColor="#00b6fd" />
          <stop offset="0.5" stopColor="#0083fc" />
          <stop offset="1" stopColor="#005dfb" />
        </radialGradient>
        {/*
          The fold starts about a third of the way down and is well into blue by
          two thirds. Measured off the source: the right arm's white is full
          width at y 47% and down to a quarter of it by y 57%, so a fade that
          only begins at the halfway point leaves the arm reading as a second
          white bar rather than as the underside of a ribbon.
        */}
        <linearGradient id={arm} x1="0.5" y1="0" x2="0.4" y2="1">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="0.22" stopColor="#ffffff" />
          <stop offset="0.45" stopColor="#cfe6fa" />
          <stop offset="0.72" stopColor="#86c0f4" />
          <stop offset="1" stopColor="#74b5f1" />
        </linearGradient>
      </defs>

      <rect width="512" height="512" rx="118" fill={`url(#${bg})`} />

      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path d="M351 182 248 340" stroke={`url(#${arm})`} strokeWidth="92" />
        <path d="M170 205 248 340" stroke="#ffffff" strokeWidth="87" />
      </g>
    </svg>
  );
}
